import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { NewIssueDialog } from "../components/NewIssueDialog.tsx";
import { IssueRow } from "../components/IssueRow.tsx";
import { IssuesSidePanel } from "../components/IssuesSidePanel.tsx";
import { IssueTable } from "../components/IssueTable.tsx";
import { SearchBox } from "../components/SearchBox.tsx";
import { TopBar } from "../components/TopBar.tsx";
import { client, useClient } from "../lib/client.ts";
import { compactCount, issueKey } from "../lib/format.ts";
import { setListContext } from "../lib/listContext.ts";
import { Menu } from "../ui/Menu.tsx";
import { Button, Pager } from "../ui/ring.ts";

const SUB = "issues";
const LIMIT = 200;
const ROW = 40;
type ViewMode = "table" | "list";

export function IssuesPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  // Page 1 is the live window; later pages are static reads of the same query (`GET /api/issues?offset=`).
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [staticPage, setStaticPage] = useState<{ page: number; q: string; ids: number[]; total: number } | null>(null);
  const [creating, setCreating] = useState(false);
  const [draftId, setDraftId] = useState<number | null>(() => (params.get("draft") ? Number(params.get("draft")) : null));
  const [panel, setPanel] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    client.openView(SUB, q, LIMIT);
    return () => client.closeView(SUB);
  }, [q]);

  const view = useClient((s) => s.views[SUB]);
  const schema = useClient((s) => s.schema);
  const mode: ViewMode = useClient((s) => s.me?.settings.issuesView) ?? "table";
  useEffect(() => {
    if (page === 1) {
      setStaticPage(null);
      return;
    }
    let cancelled = false;
    client.http
      .search(q, LIMIT, (page - 1) * LIMIT)
      .then((r) => {
        if (cancelled) return;
        client.ingest(r.issues);
        setStaticPage({ page, q, ids: r.issues.map((i) => i.id), total: r.total });
      })
      .catch((e) => !cancelled && client.pushError("search", (e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [q, page]);
  const onStatic = page > 1;
  const ids = onStatic ? (staticPage && staticPage.page === page && staticPage.q === q ? staticPage.ids : null) : (view?.ids ?? null);
  const total = view?.total ?? staticPage?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  useEffect(() => {
    if (ids) setListContext({ query: q, ids, total });
  }, [ids, q, total]);
  const errorPos = view?.error ? /\(at (\d+)\)/.exec(view.error)?.[1] : undefined;
  const setQuery = (next: string): void => setParams(next ? { q: next } : {});
  const setPage = (p: number): void => setParams({ ...(q ? { q } : {}), ...(p > 1 ? { page: String(p) } : {}) });
  const chooseMode = (m: ViewMode): void => void client.setUserSettings({ issuesView: m });
  const viewItems = [
    { key: "table", label: "Table", checked: mode === "table", onClick: () => chooseMode("table") },
    { key: "list", label: "List", checked: mode === "list", onClick: () => chooseMode("list") },
  ];
  const counted = view && !(view.status === "loading" && view.ids.length === 0);
  // Pager (Ring UI, the one YouTrack uses) below the last row; hidden when everything fits on one page.
  const footer: ReactNode = total > LIMIT && (
    <div className="px-6 py-4" aria-label="Pages">
      <Pager total={total} currentPage={page} pageSize={LIMIT} visiblePagesLimit={5} disablePageSizeSelector onPageChange={(p) => setPage(p)} />
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <TopBar
        crumbs={[{ label: "Issues" }]}
        after={
          <span className="flex items-center gap-2">
            <Menu anchor={<button className="icon-btn h-6 w-6" title="View"><ChevronDown size={16} /></button>} items={viewItems} title="View as" />
            {counted && <span className="text-[15px] text-muted tabular-nums" title={`${total} issues`}>{compactCount(total)}</span>}
          </span>
        }
        onCreate={() => setCreating(true)}
        onTogglePanel={() => setPanel((p) => !p)}
      />
      <div className="flex min-h-0 flex-1">
        {panel && <IssuesSidePanel onQuery={setQuery} currentQuery={q} onOpenDraft={(id) => setDraftId(id)} />}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center gap-2 px-6 pt-4 pb-3">
            <SearchBox size="large" className="min-w-0 flex-1" query={q} onQuery={setQuery} />
            <Menu anchor={<Button icon={SlidersHorizontal} title="View settings" className="view-settings" />} items={viewItems} title="View as" />
          </div>
          {view?.status === "error" && view.error && (
            <div className="border-b border-line px-6 py-2 text-danger">
              {view.error}
              {errorPos !== undefined && (
                <span className="ml-2 font-mono text-muted">
                  {q.slice(0, Number(errorPos))}
                  <span className="bg-danger-soft text-danger">{q.slice(Number(errorPos), Number(errorPos) + 1) || " "}</span>
                  {q.slice(Number(errorPos) + 1)}
                </span>
              )}
            </div>
          )}
          {view && (view.badge > 0 || (view.status === "loading" && view.ids.length > 0) || view.status === "stale") && (
            <div className="flex shrink-0 items-center gap-3 px-6 pb-2 text-xs text-muted">
              {view.badge > 0 && (
                <button className="rounded bg-warn-soft px-2 py-0.5 text-warn" onClick={() => client.openView(SUB, q, LIMIT)} title="Changes happened outside the visible window">
                  {view.badge} changed outside this view · refresh
                </button>
              )}
              {view.status === "loading" && view.ids.length > 0 && <span>refreshing…</span>}
              {view.status === "stale" && <span className="text-warn">offline: showing last known state</span>}
            </div>
          )}
          {ids && schema ? (
            mode === "table" ? (
              <IssueTable ids={ids} query={q} onQuery={setQuery} footer={footer} />
            ) : (
              <IssueList ids={ids} footer={footer} />
            )
          ) : (
            <div className="px-6 py-8 text-center text-muted">loading…</div>
          )}
        </div>
      </div>
      {(creating || draftId !== null) && (
        <NewIssueDialog
          draftId={draftId}
          onClose={() => {
            setCreating(false);
            setDraftId(null);
          }}
          onCreated={(issue) => {
            setCreating(false);
            setDraftId(null);
            navigate(`/issue/${issueKey(schema, issue)}`);
          }}
        />
      )}
    </div>
  );
}

function IssueList({ ids, footer }: { ids: number[]; footer?: ReactNode }) {
  const parentRef = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({ count: ids.length, getScrollElement: () => parentRef.current, estimateSize: () => ROW, overscan: 12 });
  if (ids.length === 0) return <div className="px-4 py-8 text-center text-muted">No issues match.</div>;
  return (
    <div ref={parentRef} className="min-h-0 flex-1 overflow-auto">
      <div className="relative" style={{ height: v.getTotalSize() }}>
        {v.getVirtualItems().map((row) => (
          <div key={ids[row.index]} className="absolute left-0 w-full" style={{ top: row.start, height: ROW }}>
            <IssueRow id={ids[row.index]!} />
          </div>
        ))}
      </div>
      {footer}
    </div>
  );
}
